import { useCallback } from 'react';
import { message, Modal } from 'antd';
import { addEdge, applyEdgeChanges } from '@xyflow/react';
import {
    IMAGE_NODE_WIDTH,
    IMAGE_NODE_HEIGHT,
    IMAGE_NODE_PREFIX,
    MANUAL_ADD_OFFSET_STEP,
    MANUAL_ADD_OFFSET_WRAP,
    CONNECTED_NODE_OFFSET_X,
    CONNECTED_NODE_OFFSET_Y,
    FUNCTION_NODE_INPUT_LIMITS,
    FUNCTION_SLOT_REPAINT_MASK_PARAM_KEY_MAP,
} from '../constants/canvas';
import { getFunctionNodeEdgeSlotKeys, reconcileEdgeSlotAssignment } from '../constants/edgeInputSlots';
import { FUNCTION_LABELS } from '../components/AddNodePopover';
import {
    createNodeId,
    createEdgeId,
    inferFunctionKeyFromNodePatch,
    isMultiImageFunctionKey,
    getSlotParamKey,
} from '../utils/common';
import {
    getDescendantNodeIds,
    getFunctionNodeInputLimit,
    getNodeSize,
    isGroupNode,
    wouldCreateCycle,
} from '../utils/graph';
import {
    getCreativeImgOccupiedCount,
    getAvailableEdgeSlotKeyForTarget,
    getTemplateReplaceReferenceManualCount,
    isSlotManuallyOccupied,
    syncConnectedTargetNodes,
    syncLinkedImageSourceNodes,
    extractCreativeImgManualUploads,
    mergeCreativeImgUploads,
} from '../utils/validation';
import {
    markExecutionPrimaryOutput,
} from '../utils/execution';
import { resumeFlowExecution, selectFlowExecutionPrimaryOutput } from '../execution';

/**
 * 画布节点/连线操作管理
 */
export const useCanvasNodes = ({
    nodesRef,
    edgesRef,
    setNodes,
    setEdges,
    onEdgesChange,
    setSelectedNodeIds,
    nextImageNodeNumberRef,
    setNextImageNodeNumber,
    activeGroupId,
    setActiveGroupId,
    reactFlowRef,
    canvasRef,
    manualAddCountRef,
    setConnectionAddMenu,
    isNodeLocked,
    isEdgeLocked,
    isNodeDeletable,
    areNodesLocked,
    persistCanvasBatch,
    pushHistory,
    closeConnectionAddMenu,
    closeContextMenu,
    isPmView,
    activeTool,
    isCanvasExecutionLocked,
    pendingFunctionSwitchRef,
    handleExecuteNode,
    pollGroupExecution,
    ensureCanvasProject,
}) => {
    // 更新节点数据，并同步影响到的下游节点
    const handleNodeDataChange = useCallback((nodeId, patch) => {
        if (isNodeLocked(nodeId)) return;
        const now = Date.now();
        const pendingSwitch = pendingFunctionSwitchRef.current.get(nodeId);
        if (pendingSwitch && pendingSwitch.expiresAt <= now) {
            pendingFunctionSwitchRef.current.delete(nodeId);
        }
        const activePendingSwitch = pendingFunctionSwitchRef.current.get(nodeId);
        const patchFunctionKey = inferFunctionKeyFromNodePatch(patch);
        if (
            activePendingSwitch
            && patchFunctionKey
            && patchFunctionKey !== activePendingSwitch.functionKey
        ) {
            return;
        }
        if (
            activePendingSwitch
            && patchFunctionKey
            && patchFunctionKey === activePendingSwitch.functionKey
        ) {
            pendingFunctionSwitchRef.current.delete(nodeId);
        }
        const beforeNodes = nodesRef.current;
        const beforeEdges = edgesRef.current;
        const nextLinkedSlotSources = (
            patch?.panelState?.linkedSlotSources
            && typeof patch.panelState.linkedSlotSources === 'object'
            && !Array.isArray(patch.panelState.linkedSlotSources)
        )
            ? patch.panelState.linkedSlotSources
            : null;
        const keptIncomingEdgeIds = nextLinkedSlotSources
            ? new Set(
                Object.values(nextLinkedSlotSources)
                    .map((item) => String(item?.edgeId || ''))
                    .filter(Boolean),
            )
            : null;
        const updatedNodes = beforeNodes.map((node) => (
            node.id === nodeId
                ? { ...node, data: { ...node.data, ...patch } }
                : node
        ));
        const nextEdges = keptIncomingEdgeIds
            ? beforeEdges.filter((edge) => (
                edge.target !== nodeId || keptIncomingEdgeIds.has(String(edge.id))
            ))
            : beforeEdges;
        const reverseSyncResult = syncLinkedImageSourceNodes(updatedNodes, nextEdges, nodeId);
        let nextNodes = syncConnectedTargetNodes(reverseSyncResult.nodes, nextEdges, {
            sourceNodeId: nodeId,
            targetNodeId: keptIncomingEdgeIds ? nodeId : null,
        });
        reverseSyncResult.sourceNodeIds.forEach((sourceNodeId) => {
            nextNodes = syncConnectedTargetNodes(nextNodes, nextEdges, { sourceNodeId });
        });
        if (
            JSON.stringify(nextNodes) === JSON.stringify(beforeNodes)
            && JSON.stringify(nextEdges) === JSON.stringify(beforeEdges)
        ) {
            return;
        }

        pushHistory();
        setNodes(nextNodes);
        if (nextEdges !== beforeEdges) {
            setEdges(nextEdges);
        }
        persistCanvasBatch({
            beforeNodes,
            beforeEdges,
            afterNodes: nextNodes,
            afterEdges: nextEdges,
        });
    }, [isNodeLocked, persistCanvasBatch, pushHistory, setEdges, setNodes, nodesRef, edgesRef, pendingFunctionSwitchRef]);

    // 图片加载后保持节点宽度不变，并按原始宽高比更新节点高度
    const handleImageNodeSizeChange = useCallback((nodeId, aspectRatio) => {
        const normalizedAspectRatio = Number(aspectRatio);
        if (!Number.isFinite(normalizedAspectRatio) || normalizedAspectRatio <= 0) return;

        const beforeNodes = nodesRef.current;
        const targetNode = beforeNodes.find((node) => node.id === nodeId);
        if (!targetNode || targetNode.type !== 'imageNode') return;

        const nextHeight = IMAGE_NODE_WIDTH / normalizedAspectRatio;
        const currentAspectRatio = Number(targetNode.data?.imageAspectRatio);
        const currentHeight = getNodeSize(targetNode).height;
        if (
            Math.abs(currentAspectRatio - normalizedAspectRatio) < 0.0001
            && Math.abs(currentHeight - nextHeight) < 0.5
        ) return;

        const nextNodes = beforeNodes.map((node) => (
            node.id === nodeId
                ? {
                    ...node,
                    width: IMAGE_NODE_WIDTH,
                    height: nextHeight,
                    measured: undefined,
                    style: {
                        ...(node.style || {}),
                        width: IMAGE_NODE_WIDTH,
                        height: nextHeight,
                    },
                    data: {
                        ...node.data,
                        imageAspectRatio: normalizedAspectRatio,
                    },
                }
                : node
        ));

        nodesRef.current = nextNodes;
        setNodes(nextNodes);
        if (!isPmView) {
            const currentEdges = edgesRef.current;
            persistCanvasBatch({
                beforeNodes,
                beforeEdges: currentEdges,
                afterNodes: nextNodes,
                afterEdges: currentEdges,
            });
        }
    }, [isPmView, persistCanvasBatch, setNodes, nodesRef, edgesRef]);

    // 切换功能节点配置时，清理不再适用的输入连线
    const handleSwitchFunctionNode = useCallback((nodeId, patch) => {
        if (isNodeLocked(nodeId)) return;

        const beforeNodes = nodesRef.current;
        const beforeEdges = edgesRef.current;
        const nextFunctionKey = patch?.functionKey || '';
        if (nextFunctionKey) {
            pendingFunctionSwitchRef.current.set(nodeId, {
                functionKey: nextFunctionKey,
                expiresAt: Date.now() + 800,
            });
        }
        const nextLinkedSlotSources = (
            patch?.panelState?.linkedSlotSources
            && typeof patch.panelState.linkedSlotSources === 'object'
            && !Array.isArray(patch.panelState.linkedSlotSources)
        )
            ? patch.panelState.linkedSlotSources
            : {};
        const nextLinkedUploads = Array.isArray(patch?.panelState?.linkedUploads)
            ? patch.panelState.linkedUploads
            : [];
        const desiredEdgeSlotKeyMap = new Map();
        Object.entries(nextLinkedSlotSources).forEach(([slotKey, linkedState]) => {
            const edgeId = String(linkedState?.edgeId || '');
            if (!edgeId) return;
            desiredEdgeSlotKeyMap.set(edgeId, slotKey);
        });
        if (nextFunctionKey === 'templateReplace') {
            nextLinkedUploads.forEach((linkedItem) => {
                const edgeId = String(linkedItem?.edgeId || '');
                if (!edgeId) return;
                desiredEdgeSlotKeyMap.set(edgeId, 'reference');
            });
        }
        const keptIncomingEdgeIds = new Set(
            [
                ...Object.values(nextLinkedSlotSources),
                ...nextLinkedUploads,
            ]
                .map((item) => String(item?.edgeId || ''))
                .filter(Boolean),
        );

        const updatedNodes = beforeNodes.map((node) => (
            node.id === nodeId
                ? { ...node, data: { ...node.data, ...patch } }
                : node
        ));
        const nextEdges = beforeEdges.reduce((acc, edge) => {
            if (edge.target !== nodeId) {
                acc.push(edge);
                return acc;
            }

            const edgeId = String(edge.id || '');
            if (!keptIncomingEdgeIds.has(edgeId)) {
                return acc;
            }

            const desiredSlotKey = desiredEdgeSlotKeyMap.get(edgeId) || '';
            const nextEdgeData = { ...(edge.data || {}) };

            if (desiredSlotKey) {
                nextEdgeData.inputSlotKey = desiredSlotKey;
            } else {
                delete nextEdgeData.inputSlotKey;
            }

            acc.push({
                ...edge,
                data: nextEdgeData,
            });
            return acc;
        }, []);
        const nextNodes = syncConnectedTargetNodes(updatedNodes, nextEdges, { targetNodeId: nodeId });

        pushHistory();
        setNodes(nextNodes);
        setEdges(nextEdges);
        persistCanvasBatch({
            beforeNodes,
            beforeEdges,
            afterNodes: nextNodes,
            afterEdges: nextEdges,
        });
    }, [isNodeLocked, persistCanvasBatch, pushHistory, setEdges, setNodes, nodesRef, edgesRef, pendingFunctionSwitchRef]);

    // 节点生成完成后，取消该节点的选中态
    const handleNodeGenerateComplete = useCallback((nodeId) => {
        if (!nodeId) return;

        setNodes((currentNodes) => currentNodes.map((node) => (
            node.id === nodeId
                ? { ...node, selected: false }
                : node
        )));
        setSelectedNodeIds((prev) => prev.filter((selectedId) => selectedId !== nodeId));
    }, [setNodes, setSelectedNodeIds]);

    // 切换节点结果图，并同步下游引用
    const handleNodeResultSelect = useCallback(async (nodeId, selectedOutputId) => {
        const beforeNodes = nodesRef.current;
        const beforeEdges = edgesRef.current;
        const targetNode = beforeNodes.find((node) => node.id === nodeId);
        const isManualSelectionPending = String(targetNode?.data?.latestExecution?.status || targetNode?.data?.executionStatus || '').toLowerCase() === 'manual';
        const updatedNodes = beforeNodes.map((node) => (
            node.id === nodeId
                ? {
                    ...node,
                    data: {
                        ...node.data,
                        latestExecution: node.data?.latestExecution
                            ? {
                                ...node.data.latestExecution,
                                outputs: markExecutionPrimaryOutput(
                                    node.data.latestExecution.outputs,
                                    selectedOutputId,
                                ),
                            }
                            : node.data?.latestExecution,
                        selectedOutputId,
                    },
                }
                : node
        ));
        const nextNodes = syncConnectedTargetNodes(updatedNodes, beforeEdges, { sourceNodeId: nodeId });
        const hasLocalChanged = JSON.stringify(nextNodes) !== JSON.stringify(beforeNodes);

        if (hasLocalChanged) {
            pushHistory();
            setNodes(nextNodes);
            persistCanvasBatch({
                beforeNodes,
                beforeEdges,
                afterNodes: nextNodes,
                afterEdges: beforeEdges,
            });
        }

        const latestExecution = targetNode?.data?.latestExecution || {};
        const nodeExecutionId = latestExecution?.nodeExecutionId || latestExecution?.id || '';
        if (!selectedOutputId) {
            message.warning('请选择结果图');
            return;
        }
        if (!nodeExecutionId) {
            message.error('当前节点缺少执行记录，无法提交选图');
            return;
        }

        try {
            if (isManualSelectionPending) {
                const executionId = latestExecution?.executionId || '';
                if (!executionId) {
                    message.error('当前节点缺少执行批次，无法恢复执行');
                    return;
                }
                const parentGroupId = targetNode?.parentId ? String(targetNode.parentId) : '';
                const currentNodes = nodesRef.current;
                const groupChildNodes = parentGroupId
                    ? currentNodes.filter((node) => String(node.parentId || '') === parentGroupId)
                    : [];
                const groupFunctionNodeIds = groupChildNodes
                    .filter((node) => node.type === 'functionNode')
                    .map((node) => String(node.id));
                const groupChildNodeIds = groupChildNodes.map((node) => String(node.id));

                setNodes((prev) => prev.map((node) => (
                    node.id === nodeId
                        ? {
                            ...node,
                            data: {
                                ...node.data,
                                executionStatus: 'processing',
                                activeExecutionId: executionId,
                                latestExecution: node.data?.latestExecution
                                    ? {
                                        ...node.data.latestExecution,
                                        executionId,
                                        status: 'processing',
                                    }
                                    : node.data?.latestExecution,
                            },
                        }
                        : node
                )));

                await resumeFlowExecution({
                    executionId,
                    nodeId,
                    outputId: selectedOutputId,
                });

                const processingNodes = beforeNodes.map((node) => (
                    node.id === nodeId
                        ? {
                            ...node,
                            data: {
                                ...node.data,
                                executionStatus: 'processing',
                                activeExecutionId: executionId,
                                latestExecution: node.data?.latestExecution
                                    ? {
                                        ...node.data.latestExecution,
                                        executionId,
                                        status: 'processing',
                                    }
                                    : node.data?.latestExecution,
                            },
                        }
                        : node
                ));
                persistCanvasBatch({
                    beforeNodes,
                    beforeEdges,
                    afterNodes: processingNodes,
                    afterEdges: beforeEdges,
                });

                if (parentGroupId && groupFunctionNodeIds.length > 0) {
                    pollGroupExecution(parentGroupId, String(executionId), groupFunctionNodeIds, groupChildNodeIds);
                }
                return;
            }

            await selectFlowExecutionPrimaryOutput({
                nodeExecutionId,
                outputId: selectedOutputId,
            }); 
        } catch (error) {
            console.error(error);
            if (isManualSelectionPending) {
                setNodes(beforeNodes);
                if (hasLocalChanged) {
                    persistCanvasBatch({
                        beforeNodes: nextNodes,
                        beforeEdges,
                        afterNodes: beforeNodes,
                        afterEdges: beforeEdges,
                    });
                }
            }
        }
    }, [persistCanvasBatch, pollGroupExecution, pushHistory, setNodes, nodesRef, edgesRef]);

    // 创建节点连线，并自动分配输入槽位
    const handleConnect = useCallback((params) => {
        closeConnectionAddMenu();
        if (isNodeLocked(params.source) || isNodeLocked(params.target)) return;
        const beforeNodes = nodesRef.current;
        const beforeEdges = edgesRef.current;
        const sourceNode = beforeNodes.find((node) => node.id === params.source);
        const targetNode = nodesRef.current.find((node) => node.id === params.target);
        if (
            params.source
            && params.target
            && String(params.source) === String(params.target)
            && sourceNode?.type === 'functionNode'
            && targetNode?.type === 'functionNode'
        ) {
            return;
        }
        if (wouldCreateCycle(beforeEdges, params.source, params.target)) {
            message.warning('不能连接成环');
            return;
        }
        const slotKeys = targetNode?.type === 'functionNode'
            ? getFunctionNodeEdgeSlotKeys(targetNode.data?.functionKey, targetNode)
            : [];
        const nextSlotKey = slotKeys.length > 0
            ? getAvailableEdgeSlotKeyForTarget({
                edges: beforeEdges,
                targetNode,
                slotKeys,
            })
            : null;
        if (targetNode?.type === 'functionNode') {
            const inputLimit = targetNode.data?.functionKey === 'templateReplace'
                ? FUNCTION_NODE_INPUT_LIMITS.templateReplace
                : (
                    slotKeys.length > 0
                        ? slotKeys.length
                        : getFunctionNodeInputLimit(targetNode.data?.functionKey)
                );
            const incomingCount = beforeEdges.filter((edge) => edge.target === params.target).length;
            const occupiedCount = isMultiImageFunctionKey(targetNode.data?.functionKey)
                ? getCreativeImgOccupiedCount(targetNode, beforeEdges)
                : incomingCount;

            if ((slotKeys.length > 0 && !nextSlotKey) || occupiedCount >= inputLimit) {
                message.warning('没有可以串联的位置');
                return;
            }
        }

        pushHistory();
        const edgeId = createEdgeId();
        const addedEdges = addEdge({ ...params, id: edgeId, type: 'flowEdge' }, beforeEdges);
        const nextEdges = slotKeys.length === 0
            ? addedEdges
            : (
                targetNode?.data?.functionKey === 'templateReplace'
                    ? addedEdges.map((edge) => (
                        edge.id === edgeId
                            ? {
                                ...edge,
                                data: {
                                    ...edge.data,
                                    inputSlotKey: nextSlotKey,
                                },
                            }
                            : edge
                    ))
                    : reconcileEdgeSlotAssignment({
                        edges: addedEdges,
                        edgeId,
                        targetId: params.target,
                        slotKeys,
                        preferredSlotKey: nextSlotKey,
                    })
            );
        const nextNodes = syncConnectedTargetNodes(beforeNodes, nextEdges, {
            sourceNodeId: params.source,
            targetNodeId: params.target,
        });

        setNodes(nextNodes);
        setEdges(nextEdges);
        persistCanvasBatch({
            beforeNodes,
            beforeEdges,
            afterNodes: nextNodes,
            afterEdges: nextEdges,
        });
    }, [closeConnectionAddMenu, isNodeLocked, persistCanvasBatch, pushHistory, setEdges, setNodes, nodesRef, edgesRef]);

    // 手动向画布中新增节点
    const handleAddNode = useCallback(async (nodeType, options = {}) => {
        if (isPmView) return;
        const reactFlow = reactFlowRef.current;
        const canvas = canvasRef.current;
        if (!reactFlow || !canvas) return;
        const {
            autoOpenFunctionPanel = false,
            dataPatch = null,
        } = options;
        const projectContext = await ensureCanvasProject();
        const beforeNodes = nodesRef.current;
        const beforeEdges = edgesRef.current;
        pushHistory();

        const bounds = canvas.getBoundingClientRect();
        const centerPosition = reactFlow.screenToFlowPosition({
            x: bounds.left + bounds.width / 2,
            y: bounds.top + bounds.height / 2,
        });
        const addIndex = manualAddCountRef.current;
        const offsetIndex = addIndex % MANUAL_ADD_OFFSET_WRAP;
        const rowIndex = Math.floor(addIndex / MANUAL_ADD_OFFSET_WRAP);
        const manualOffset = {
            x: offsetIndex * MANUAL_ADD_OFFSET_STEP,
            y: rowIndex * MANUAL_ADD_OFFSET_STEP,
        };
        manualAddCountRef.current += 1;

        if (nodeType === 'image') {
            const nodeId = createNodeId('image-node');
            const nextNodes = beforeNodes.concat({
                id: nodeId,
                type: 'imageNode',
                position: {
                    x: centerPosition.x - IMAGE_NODE_WIDTH / 2 + manualOffset.x,
                    y: centerPosition.y - IMAGE_NODE_HEIGHT / 2 + manualOffset.y,
                },
                data: {
                    label: `${IMAGE_NODE_PREFIX}${nextImageNodeNumberRef.current}`,
                    imageSequence: nextImageNodeNumberRef.current,
                    imageSequenceLabel: `${IMAGE_NODE_PREFIX}${nextImageNodeNumberRef.current}`,
                    src: '',
                    inputParams: {
                        output: '',
                    },
                },
            });
            setNodes(nextNodes);
            nodesRef.current = nextNodes;
            setNextImageNodeNumber((prev) => prev + 1);
            await persistCanvasBatch({
                beforeNodes,
                beforeEdges,
                afterNodes: nextNodes,
                afterEdges: beforeEdges,
                projectId: projectContext.projectId,
            });
            return {
                nodeId,
                projectId: projectContext.projectId,
            };
        } else if (FUNCTION_LABELS[nodeType]) {
            const nodeId = createNodeId('func');
            const nextNodes = (autoOpenFunctionPanel
                ? beforeNodes.map((node) => ({
                    ...node,
                    selected: false,
                }))
                : beforeNodes
            ).concat({
                id: nodeId,
                type: 'functionNode',
                selected: autoOpenFunctionPanel,
                position: {
                    x: centerPosition.x - IMAGE_NODE_WIDTH / 2 + manualOffset.x,
                    y: centerPosition.y - IMAGE_NODE_HEIGHT / 2 + manualOffset.y,
                },
                data: {
                    label: FUNCTION_LABELS[nodeType],
                    functionKey: nodeType,
                    imageSequence: nextImageNodeNumberRef.current,
                    imageSequenceLabel: `${IMAGE_NODE_PREFIX}${nextImageNodeNumberRef.current}`,
                    ...(dataPatch && typeof dataPatch === 'object' ? dataPatch : {}),
                },
            });
            setNodes(nextNodes);
            nodesRef.current = nextNodes;
            if (autoOpenFunctionPanel) {
                setSelectedNodeIds([nodeId]);
            }
            setNextImageNodeNumber((prev) => prev + 1);
            await persistCanvasBatch({
                beforeNodes,
                beforeEdges,
                afterNodes: nextNodes,
                afterEdges: beforeEdges,
                projectId: projectContext.projectId,
            });
            return {
                nodeId,
                projectId: projectContext.projectId,
            };
        }
    }, [canvasRef, ensureCanvasProject, isPmView, manualAddCountRef, persistCanvasBatch, pushHistory, reactFlowRef, setNodes, nodesRef, edgesRef, nextImageNodeNumberRef, setNextImageNodeNumber, setSelectedNodeIds]);

    // 从现有节点快速追加下游功能节点
    const handleQuickAddNode = useCallback((sourceNodeId, nodeType, options = {}) => {
        if (isPmView) return;
        if (isNodeLocked(sourceNodeId)) return;
        if (!FUNCTION_LABELS[nodeType]) return;

        const sourceNode = nodesRef.current.find((node) => node.id === sourceNodeId);
        if (!sourceNode) return;
        const beforeNodes = nodesRef.current;
        const beforeEdges = edgesRef.current;
        const requestedPosition = options.flowPosition;

        pushHistory();

        const nodeId = createNodeId('func');
        const createdNodes = beforeNodes.map((node) => ({ ...node, selected: false })).concat({
            id: nodeId,
            type: 'functionNode',
            selected: true,
            position: requestedPosition
                ? {
                    x: requestedPosition.x,
                    y: requestedPosition.y - IMAGE_NODE_HEIGHT / 2,
                }
                : {
                    x: sourceNode.position.x + CONNECTED_NODE_OFFSET_X,
                    y: sourceNode.position.y + CONNECTED_NODE_OFFSET_Y,
                },
            data: {
                label: FUNCTION_LABELS[nodeType],
                functionKey: nodeType,
                imageSequence: nextImageNodeNumberRef.current,
                imageSequenceLabel: `${IMAGE_NODE_PREFIX}${nextImageNodeNumberRef.current}`,
            },
        });
        const nextEdges = beforeEdges.map((edge) => ({ ...edge, selected: false })).concat({
            id: createEdgeId(),
            source: sourceNodeId,
            target: nodeId,
            type: 'flowEdge',
            data: (() => {
                const slotKeys = getFunctionNodeEdgeSlotKeys(nodeType);
                return slotKeys.length > 0
                    ? { inputSlotKey: slotKeys[0] }
                    : undefined;
            })(),
        });
        const nextNodes = syncConnectedTargetNodes(createdNodes, nextEdges, {
            sourceNodeId,
            targetNodeId: nodeId,
        });

        setNodes(nextNodes);
        setEdges(nextEdges);
        setSelectedNodeIds([nodeId]);
        setNextImageNodeNumber((prev) => prev + 1);
        persistCanvasBatch({
            beforeNodes,
            beforeEdges,
            afterNodes: nextNodes,
            afterEdges: nextEdges,
        });
    }, [isNodeLocked, isPmView, persistCanvasBatch, pushHistory, setEdges, setNodes, nodesRef, edgesRef, nextImageNodeNumberRef, setNextImageNodeNumber, setSelectedNodeIds]);

    // 连线释放在空白处时，在末端显示“增加节点”菜单
    const handleConnectEnd = useCallback((event, connectionState) => {
        if (isPmView || activeTool !== 'select' || isCanvasExecutionLocked) return;
        if (
            connectionState?.isValid
            || connectionState?.toNode
            || connectionState?.fromHandle?.type !== 'source'
        ) return;

        const sourceNodeId = String(connectionState?.fromNode?.id || connectionState?.fromHandle?.nodeId || '');
        if (!sourceNodeId || isNodeLocked(sourceNodeId)) return;

        const touch = event?.changedTouches?.[0] || event?.touches?.[0];
        const clientX = Number(touch?.clientX ?? event?.clientX);
        const clientY = Number(touch?.clientY ?? event?.clientY);
        const reactFlow = reactFlowRef.current;
        if (!reactFlow || !Number.isFinite(clientX) || !Number.isFinite(clientY)) return;

        const flowPosition = reactFlow.screenToFlowPosition({ x: clientX, y: clientY });
        const menuWidth = 240;
        const menuHeight = 360;

        closeContextMenu();
        setConnectionAddMenu({
            open: true,
            x: Math.max(8, Math.min(clientX + 8, window.innerWidth - menuWidth)),
            y: Math.max(8, Math.min(clientY + 8, window.innerHeight - menuHeight)),
            sourceNodeId,
            flowPosition,
        });
    }, [activeTool, closeContextMenu, isCanvasExecutionLocked, isNodeLocked, isPmView, reactFlowRef, setConnectionAddMenu]);

    // 执行节点或连线删除，并修正受影响的关联数据
    const performDelete = useCallback((nodeIds, edgeIds = [], options = {}) => {
        const { forceNodeIds = [] } = options;
        const currentNodes = nodesRef.current;
        const currentEdges = edgesRef.current;
        const forcedNodeIdSet = new Set(forceNodeIds || []);
        const deletableNodeIds = new Set((nodeIds || []).filter((nodeId) => (
            forcedNodeIdSet.has(nodeId) || isNodeDeletable(nodeId)
        )));
        const deletableEdgeIds = new Set((edgeIds || []).filter((edgeId) => !isEdgeLocked(edgeId)));

        if (deletableNodeIds.size === 0 && deletableEdgeIds.size === 0) return;

        if (activeGroupId && deletableNodeIds.has(activeGroupId)) {
            setActiveGroupId(null);
        }

        let nextNodes = currentNodes.filter((node) => !deletableNodeIds.has(node.id));
        const nextEdges = currentEdges.filter((edge) => (
            !deletableEdgeIds.has(edge.id)
            && !deletableNodeIds.has(edge.source)
            && !deletableNodeIds.has(edge.target)
        ));
        const affectedTargetNodeIds = Array.from(new Set(
            currentEdges
                .filter((edge) => (
                    !nextEdges.some((nextEdge) => nextEdge.id === edge.id)
                    && !deletableNodeIds.has(edge.target)
                ))
                .map((edge) => edge.target),
        ));

        affectedTargetNodeIds.forEach((targetNodeId) => {
            nextNodes = syncConnectedTargetNodes(nextNodes, nextEdges, { targetNodeId });
        });

        pushHistory();
        setNodes(nextNodes);
        setEdges(nextEdges);
        persistCanvasBatch({
            beforeNodes: currentNodes,
            beforeEdges: currentEdges,
            afterNodes: nextNodes,
            afterEdges: nextEdges,
        });
    }, [activeGroupId, isEdgeLocked, isNodeDeletable, persistCanvasBatch, pushHistory, setEdges, setNodes, nodesRef, edgesRef, setActiveGroupId]);

    // 删除分组前弹出二次确认
    const confirmDeleteGroupsIfNeeded = useCallback((groupNodeIds, onConfirm) => {
        if (!groupNodeIds.length) {
            onConfirm?.();
            return;
        }

        const groupNodes = nodesRef.current.filter((node) => groupNodeIds.includes(node.id));
        const groupLabels = groupNodes.map((node) => node.data?.label || '组合');
        const title = groupLabels.length === 1 ? `确认删除${groupLabels[0]}吗？` : `确认删除这${groupLabels.length}个组合吗？`;
        const content = '删除组合会同时删除组内节点和连线，此操作不可恢复。';

        Modal.confirm({
            title,
            content,
            okText: '删除',
            cancelText: '取消',
            okButtonProps: { danger: true },
            onOk: () => onConfirm?.(),
        });
    }, [nodesRef]);

    // 删除当前选中的节点或连线
    const handleDeleteSelection = useCallback(() => {
        const currentNodes = nodesRef.current;
        const currentEdges = edgesRef.current;
        const selectedNodeIds = getDescendantNodeIds(
            currentNodes.filter((node) => node.selected).map((node) => node.id),
            currentNodes,
        );
        const deletableNodeIds = Array.from(selectedNodeIds).filter((nodeId) => isNodeDeletable(nodeId));
        const selectedEdgeIds = Array.from(
            currentEdges
                .filter((edge) => edge.selected && !isEdgeLocked(edge.id))
                .map((edge) => edge.id),
        );

        if (deletableNodeIds.length === 0 && selectedEdgeIds.length === 0) return;

        const groupNodeIds = deletableNodeIds.filter((nodeId) => {
            const node = currentNodes.find((item) => item.id === nodeId);
            return isGroupNode(node);
        });
        const forcedGroupDescendantNodeIds = Array.from(getDescendantNodeIds(groupNodeIds, currentNodes));
        const finalNodeIds = Array.from(new Set(deletableNodeIds.concat(forcedGroupDescendantNodeIds)));

        confirmDeleteGroupsIfNeeded(groupNodeIds, () => {
            performDelete(finalNodeIds, selectedEdgeIds, {
                forceNodeIds: forcedGroupDescendantNodeIds,
            });
        });
    }, [confirmDeleteGroupsIfNeeded, isEdgeLocked, isNodeDeletable, performDelete, nodesRef, edgesRef]);

    // 删除指定节点，分组节点会连带处理子节点
    const handleDeleteNode = useCallback((nodeId) => {
        const targetNode = nodesRef.current.find((node) => node.id === nodeId);
        if (!targetNode) return;
        if (!isNodeDeletable(nodeId)) return;

        const selectedNodeIds = Array.from(getDescendantNodeIds([nodeId], nodesRef.current));
        const confirmAndDelete = () => performDelete(selectedNodeIds, [], {
            forceNodeIds: isGroupNode(targetNode) ? selectedNodeIds : [],
        });

        if (isGroupNode(targetNode)) {
            confirmDeleteGroupsIfNeeded([nodeId], confirmAndDelete);
            return;
        }

        confirmAndDelete();
    }, [confirmDeleteGroupsIfNeeded, isNodeDeletable, performDelete, nodesRef]);

    // 从画布移除连线，并同步清理目标节点引用
    const removeEdgesFromCanvas = useCallback((edgeIds, options = {}) => {
        const normalizedEdgeIds = Array.from(new Set((edgeIds || []).filter(Boolean)));
        if (normalizedEdgeIds.length === 0) return;
        if (normalizedEdgeIds.some((edgeId) => isEdgeLocked(edgeId))) return;

        const beforeNodes = nodesRef.current;
        const beforeEdges = edgesRef.current;
        const removedEdgeIdSet = new Set(normalizedEdgeIds);
        const removedEdges = beforeEdges.filter((edge) => removedEdgeIdSet.has(edge.id));
        const nextEdges = beforeEdges.filter((edge) => !removedEdgeIdSet.has(edge.id));

        if (nextEdges.length === beforeEdges.length) return;

        let nextNodes = beforeNodes;

        removedEdges.forEach((targetEdge) => {
            nextNodes = syncConnectedTargetNodes(nextNodes, nextEdges, {
                sourceNodeId: targetEdge.source,
                targetNodeId: targetEdge.target,
            });

            if (!targetEdge?.target) return;

            const targetNodeId = String(targetEdge.target);
            const fallbackSlotKey = targetEdge.data?.inputSlotKey || '';
            const previousTargetNode = beforeNodes.find((node) => String(node.id) === targetNodeId);
            const previousLinkedSlotSources = previousTargetNode?.data?.panelState?.linkedSlotSources
                && typeof previousTargetNode.data.panelState.linkedSlotSources === 'object'
                && !Array.isArray(previousTargetNode.data.panelState.linkedSlotSources)
                ? previousTargetNode.data.panelState.linkedSlotSources
                : {};
            const isTemplateReplaceNode = previousTargetNode?.data?.functionKey === 'templateReplace';
            const matchedSlotKeys = Object.entries(previousLinkedSlotSources)
                .filter(([, linkedState]) => String(linkedState?.edgeId || '') === String(targetEdge.id))
                .map(([linkedSlotKey]) => linkedSlotKey);
            const slotKeysToClear = isTemplateReplaceNode
                ? []
                : (
                    matchedSlotKeys.length > 0
                        ? matchedSlotKeys
                        : (fallbackSlotKey ? [fallbackSlotKey] : [])
                );

            nextNodes = nextNodes.map((node) => {
                if (String(node.id) !== targetNodeId || node.type !== 'functionNode') {
                    return node;
                }

                const previousPanelState = node.data?.panelState || {};
                const previousUploads = previousPanelState.uploads && typeof previousPanelState.uploads === 'object'
                    ? previousPanelState.uploads
                    : {};
                const currentLinkedSlotSources = previousPanelState.linkedSlotSources
                    && typeof previousPanelState.linkedSlotSources === 'object'
                    && !Array.isArray(previousPanelState.linkedSlotSources)
                    ? previousPanelState.linkedSlotSources
                    : {};
                if (slotKeysToClear.length === 0) {
                    return node;
                }

                const nextUploads = {
                    ...previousUploads,
                };
                const nextInputParams = {
                    ...(node.data?.inputParams || {}),
                };
                const nextLinkedSlotSources = {
                    ...currentLinkedSlotSources,
                };

                slotKeysToClear.forEach((linkedSlotKey) => {
                    const paramKey = getSlotParamKey(node.data?.functionKey, linkedSlotKey);
                    nextUploads[linkedSlotKey] = '';
                    if (paramKey) {
                        nextInputParams[paramKey] = '';
                    }
                    delete nextLinkedSlotSources[linkedSlotKey];
                });

                return {
                    ...node,
                    data: {
                        ...node.data,
                        inputParams: nextInputParams,
                        panelState: {
                            ...previousPanelState,
                            uploads: nextUploads,
                            linkedSlotSources: nextLinkedSlotSources,
                        },
                    },
                };
            });
        });

        pushHistory();
        setNodes(nextNodes);
        setEdges(options.edgeChanges
            ? applyEdgeChanges(options.edgeChanges, nextEdges)
            : nextEdges);
        persistCanvasBatch({
            beforeNodes,
            beforeEdges,
            afterNodes: nextNodes,
            afterEdges: nextEdges,
        });
    }, [isEdgeLocked, persistCanvasBatch, pushHistory, setEdges, setNodes, nodesRef, edgesRef]);

    // 删除单条连线
    const handleDeleteEdge = useCallback((edgeId) => {
        removeEdgesFromCanvas([edgeId]);
    }, [removeEdgesFromCanvas]);

    // 统一处理 ReactFlow 的连线变更事件
    const handleEdgesChange = useCallback((changes) => {
        const removeIds = changes
            .filter((change) => change.type === 'remove')
            .map((change) => change.id)
            .filter(Boolean);
        const nonRemoveChanges = changes.filter((change) => change.type !== 'remove');

        if (removeIds.length > 0) {
            removeEdgesFromCanvas(removeIds, { edgeChanges: nonRemoveChanges });
            return;
        }

        onEdgesChange(changes);
    }, [onEdgesChange, removeEdgesFromCanvas]);

    // 删除功能节点槽位上通过连线关联的图片
    const handleRemoveFunctionLinkedUpload = useCallback((targetNodeId, slotKey, linkedState) => {
        if (!targetNodeId || !slotKey) return;
        const targetNode = nodesRef.current.find((node) => node.id === targetNodeId);
        if (!targetNode || isNodeLocked(targetNodeId)) return;

        const fallbackEdge = edgesRef.current.find((edge) => (
            String(edge.target) === String(targetNodeId)
            && String(edge.data?.inputSlotKey || '') === String(slotKey)
            && (
                !linkedState?.sourceNodeId
                || String(edge.source) === String(linkedState.sourceNodeId)
            )
        ));
        const resolvedEdgeId = linkedState?.edgeId || fallbackEdge?.id || '';
        if (!resolvedEdgeId) return;

        handleDeleteEdge(resolvedEdgeId);
    }, [handleDeleteEdge, isNodeLocked, nodesRef, edgesRef]);

    // 图片节点连入时替换参数图会反向同步图片节点；算法节点连入时则断开旧连线。
    const handleReplaceFunctionLinkedUpload = useCallback((targetNodeId, slotKey, linkedState, uploadedUrl) => {
        if (!targetNodeId || !slotKey || !uploadedUrl) return;

        const beforeNodes = nodesRef.current;
        const beforeEdges = edgesRef.current;
        const targetNode = beforeNodes.find((node) => String(node.id) === String(targetNodeId));
        if (!targetNode || isNodeLocked(targetNodeId)) return;

        const fallbackEdge = beforeEdges.find((edge) => (
            String(edge.target) === String(targetNodeId)
            && String(edge.data?.inputSlotKey || '') === String(slotKey)
            && (
                !linkedState?.sourceNodeId
                || String(edge.source) === String(linkedState.sourceNodeId)
            )
        ));
        const resolvedEdgeId = linkedState?.edgeId || fallbackEdge?.id || '';
        const targetEdge = beforeEdges.find((edge) => String(edge.id) === String(resolvedEdgeId));
        if (!targetEdge || isEdgeLocked(targetEdge.id)) return;

        const sourceNode = beforeNodes.find((node) => String(node.id) === String(targetEdge.source));
        const shouldDisconnect = sourceNode?.type === 'functionNode';
        const nextEdges = shouldDisconnect
            ? beforeEdges.filter((edge) => String(edge.id) !== String(targetEdge.id))
            : beforeEdges;
        let nextNodes = shouldDisconnect
            ? syncConnectedTargetNodes(beforeNodes, nextEdges, {
                sourceNodeId: targetEdge.source,
                targetNodeId: targetEdge.target,
            })
            : beforeNodes;

        nextNodes = nextNodes.map((node) => {
            if (String(node.id) !== String(targetNodeId) || node.type !== 'functionNode') return node;

            const previousPanelState = node.data?.panelState || {};
            const previousUploads = previousPanelState.uploads
                && typeof previousPanelState.uploads === 'object'
                && !Array.isArray(previousPanelState.uploads)
                ? previousPanelState.uploads
                : {};
            const previousLinkedSlotSources = previousPanelState.linkedSlotSources
                && typeof previousPanelState.linkedSlotSources === 'object'
                && !Array.isArray(previousPanelState.linkedSlotSources)
                ? previousPanelState.linkedSlotSources
                : {};
            const nextLinkedSlotSources = { ...previousLinkedSlotSources };
            if (shouldDisconnect) {
                delete nextLinkedSlotSources[slotKey];
            } else if (nextLinkedSlotSources[slotKey]) {
                nextLinkedSlotSources[slotKey] = {
                    ...nextLinkedSlotSources[slotKey],
                    url: uploadedUrl,
                    custom: true,
                };
            }
            const paramKey = getSlotParamKey(node.data?.functionKey, slotKey);
            const isTemplateReplace = node.data?.functionKey === 'templateReplace';
            const nextManualUploads = isTemplateReplace
                ? (Array.isArray(previousPanelState.manualUploads)
                    ? previousPanelState.manualUploads.slice()
                    : [node.data?.inputParams?.srcImage || '', node.data?.inputParams?.refImage || ''])
                : null;
            if (isTemplateReplace && shouldDisconnect) {
                nextManualUploads[slotKey === 'source' ? 0 : 1] = uploadedUrl;
            }

            return {
                ...node,
                data: {
                    ...node.data,
                    inputParams: {
                        ...(node.data?.inputParams || {}),
                        ...(paramKey ? { [paramKey]: uploadedUrl } : {}),
                    },
                    panelState: {
                        ...previousPanelState,
                        ...(isTemplateReplace
                            ? { manualUploads: nextManualUploads }
                            : {
                                uploads: {
                                    ...previousUploads,
                                    [slotKey]: uploadedUrl,
                                },
                            }),
                        linkedSlotSources: nextLinkedSlotSources,
                    },
                },
            };
        });

        if (!shouldDisconnect) {
            const reverseSyncResult = syncLinkedImageSourceNodes(nextNodes, beforeEdges, targetNodeId);
            nextNodes = reverseSyncResult.nodes;
            reverseSyncResult.sourceNodeIds.forEach((sourceNodeId) => {
                nextNodes = syncConnectedTargetNodes(nextNodes, beforeEdges, { sourceNodeId });
            });
        }

        pushHistory();
        setNodes(nextNodes);
        setEdges(nextEdges);
        persistCanvasBatch({
            beforeNodes,
            beforeEdges,
            afterNodes: nextNodes,
            afterEdges: nextEdges,
        });
    }, [isEdgeLocked, isNodeLocked, persistCanvasBatch, pushHistory, setEdges, setNodes, nodesRef, edgesRef]);

    // 删除创意节点上通过连线关联的图片
    const handleRemoveCreativeLinkedUpload = useCallback((targetNodeId, linkedState) => {
        if (!targetNodeId) return;
        const targetNode = nodesRef.current.find((node) => node.id === targetNodeId);
        if (!targetNode || isNodeLocked(targetNodeId)) return;

        const resolvedEdgeId = linkedState?.edgeId || '';
        if (!resolvedEdgeId) return;

        handleDeleteEdge(resolvedEdgeId);
    }, [handleDeleteEdge, isNodeLocked, nodesRef]);

    // 图片节点连入时反向同步源图；功能节点互联时替换图片会解除当前连线
    const handleReplaceCreativeLinkedUpload = useCallback((targetNodeId, linkedState, uploadedUrls = [], targetIndex = 0) => {
        if (!targetNodeId || !linkedState?.edgeId) return;
        if (isNodeLocked(targetNodeId) || isEdgeLocked(linkedState.edgeId)) return;

        const validUploadedUrls = (Array.isArray(uploadedUrls) ? uploadedUrls : [])
            .filter(Boolean)
            .slice(0, FUNCTION_NODE_INPUT_LIMITS.creativeImg2Img);
        if (validUploadedUrls.length === 0) return;

        const beforeNodes = nodesRef.current;
        const beforeEdges = edgesRef.current;
        const targetNode = beforeNodes.find((node) => String(node.id) === String(targetNodeId));
        if (!targetNode || targetNode.type !== 'functionNode' || !isMultiImageFunctionKey(targetNode.data?.functionKey)) return;

        const targetEdgeId = String(linkedState.edgeId);
        const targetEdge = beforeEdges.find((edge) => String(edge.id) === targetEdgeId);
        if (!targetEdge) return;
        const sourceNode = beforeNodes.find((node) => String(node.id) === String(targetEdge.source));

        const previousPanelState = targetNode.data?.panelState || {};
        const previousLinkedUploads = Array.isArray(previousPanelState.linkedUploads)
            ? previousPanelState.linkedUploads
            : [];
        const previousManualUploads = Array.isArray(previousPanelState.manualUploads)
            ? previousPanelState.manualUploads.filter(Boolean)
            : extractCreativeImgManualUploads(
                Array.isArray(previousPanelState.uploads)
                    ? previousPanelState.uploads
                    : (Array.isArray(targetNode.data?.inputParams?.imageUrls)
                        ? targetNode.data.inputParams.imageUrls
                        : []),
                previousLinkedUploads,
            );
        const linkedUploadIndex = previousLinkedUploads.findIndex((item) => String(item?.edgeId || '') === targetEdgeId);
        if (linkedUploadIndex < 0) return;

        const previousOrder = Array.isArray(previousPanelState.uploadItems)
            ? previousPanelState.uploadItems
            : Array.isArray(previousPanelState.uploads)
            ? previousPanelState.uploads.filter(Boolean)
            : [];
        const fallbackOrder = mergeCreativeImgUploads(previousManualUploads, previousLinkedUploads, previousOrder);
        const replaceIndex = Math.min(
            Math.max(Number.isFinite(targetIndex) ? targetIndex : 0, 0),
            Math.max(fallbackOrder.length - 1, 0),
        );
        const fallbackUploadItems = fallbackOrder.map((url) => {
            const linkedUpload = previousLinkedUploads.find((item) => item?.url === url);
            return linkedUpload?.edgeId
                ? { type: 'linked', edgeId: linkedUpload.edgeId, url }
                : { type: 'manual', url };
        });
        const nextUploadItems = (Array.isArray(previousPanelState.uploadItems)
            ? previousPanelState.uploadItems.slice()
            : fallbackUploadItems
        );

        if (sourceNode?.type === 'functionNode') {
            const nextLinkedUploads = previousLinkedUploads.filter((_, index) => index !== linkedUploadIndex);
            const nextManualUploads = previousManualUploads
                .concat(validUploadedUrls)
                .slice(0, FUNCTION_NODE_INPUT_LIMITS.creativeImg2Img);
            nextUploadItems.splice(
                replaceIndex,
                1,
                ...validUploadedUrls.map((url) => ({ type: 'manual', url })),
            );
            const normalizedUploadItems = nextUploadItems.slice(0, FUNCTION_NODE_INPUT_LIMITS.creativeImg2Img);
            const nextMergedUploads = mergeCreativeImgUploads(nextManualUploads, nextLinkedUploads, normalizedUploadItems);
            const updatedNodes = beforeNodes.map((node) => (
                String(node.id) !== String(targetNodeId)
                    ? node
                    : {
                        ...node,
                        data: {
                            ...node.data,
                            inputParams: {
                                ...(node.data?.inputParams || {}),
                                imageUrls: nextMergedUploads,
                                srcImage: nextMergedUploads[0] || '',
                                refImage: nextMergedUploads[1] || '',
                            },
                            panelState: {
                                ...previousPanelState,
                                manualUploads: nextManualUploads,
                                linkedUploads: nextLinkedUploads,
                                uploadItems: normalizedUploadItems,
                                uploads: nextMergedUploads,
                            },
                        },
                    }
            ));
            const nextEdges = beforeEdges.filter((edge) => String(edge.id) !== targetEdgeId);

            pushHistory();
            setNodes(updatedNodes);
            setEdges(nextEdges);
            persistCanvasBatch({ beforeNodes, beforeEdges, afterNodes: updatedNodes, afterEdges: nextEdges });
            return;
        }

        const nextLinkedUploads = previousLinkedUploads.map((item, index) => (
            index === linkedUploadIndex
                ? { ...item, url: validUploadedUrls[0], custom: true }
                : item
        ));
        const nextMergedUploads = fallbackOrder.slice();
        nextMergedUploads[replaceIndex] = validUploadedUrls[0];
        nextUploadItems[replaceIndex] = { type: 'linked', edgeId: targetEdgeId, url: validUploadedUrls[0] };
        const updatedNodes = beforeNodes.map((node) => {
            if (String(node.id) !== String(targetNodeId)) return node;

            return {
                ...node,
                data: {
                    ...node.data,
                    inputParams: {
                        ...(node.data?.inputParams || {}),
                        imageUrls: nextMergedUploads,
                        srcImage: nextMergedUploads[0] || '',
                        refImage: nextMergedUploads[1] || '',
                    },
                    panelState: {
                        ...previousPanelState,
                        manualUploads: previousManualUploads,
                        linkedUploads: nextLinkedUploads,
                        uploadItems: nextUploadItems,
                        uploads: nextMergedUploads,
                    },
                },
            };
        });

        const reverseSyncResult = syncLinkedImageSourceNodes(updatedNodes, beforeEdges, targetNodeId);
        let nextNodes = reverseSyncResult.nodes;
        reverseSyncResult.sourceNodeIds.forEach((sourceNodeId) => {
            nextNodes = syncConnectedTargetNodes(nextNodes, beforeEdges, { sourceNodeId });
        });

        pushHistory();
        setNodes(nextNodes);
        persistCanvasBatch({
            beforeNodes,
            beforeEdges,
            afterNodes: nextNodes,
            afterEdges: beforeEdges,
        });
    }, [isEdgeLocked, isNodeLocked, persistCanvasBatch, pushHistory, setEdges, setNodes, nodesRef, edgesRef]);

    // 调整连线绑定到目标节点的输入槽位
    const handleEdgeSlotChange = useCallback((edgeId, nextSlotKey) => {
        if (isEdgeLocked(edgeId)) return;
        const beforeNodes = nodesRef.current;
        const beforeEdges = edgesRef.current;
        const targetEdge = beforeEdges.find((edge) => edge.id === edgeId);
        if (!targetEdge) return;

        const targetNode = beforeNodes.find((node) => node.id === targetEdge.target);
        const slotKeys = getFunctionNodeEdgeSlotKeys(targetNode?.data?.functionKey, targetNode);
        if (slotKeys.length === 0) return;
        if (
            targetNode?.data?.functionKey === 'templateReplace'
            && nextSlotKey === 'reference'
            && getTemplateReplaceReferenceManualCount(targetNode) >= (FUNCTION_NODE_INPUT_LIMITS.templateReplace - 1)
        ) {
            message.warning('参考图最多支持13张，已满无法切换');
            return;
        }
        if (isSlotManuallyOccupied(targetNode, nextSlotKey)) {
            message.warning('该上传框已手动上传图片，无法连接');
            return;
        }

        const nextEdges = reconcileEdgeSlotAssignment({
            edges: beforeEdges,
            edgeId,
            targetId: targetEdge.target,
            slotKeys,
            preferredSlotKey: nextSlotKey,
        });
        const nextNodes = syncConnectedTargetNodes(beforeNodes, nextEdges, {
            sourceNodeId: targetEdge.source,
            targetNodeId: targetEdge.target,
        });

        if (
            JSON.stringify(nextEdges) === JSON.stringify(beforeEdges)
            && JSON.stringify(nextNodes) === JSON.stringify(beforeNodes)
        ) return;

        pushHistory();
        setNodes(nextNodes);
        setEdges(nextEdges);
        persistCanvasBatch({
            beforeNodes,
            beforeEdges,
            afterNodes: nextNodes,
            afterEdges: nextEdges,
        });
    }, [isEdgeLocked, persistCanvasBatch, pushHistory, setEdges, setNodes, nodesRef, edgesRef]);

    const handleSwapFunctionInputSlots = useCallback((targetNodeId, fromSlotKey, toSlotKey) => {
        if (!targetNodeId || !fromSlotKey || !toSlotKey || fromSlotKey === toSlotKey) return;
        if (isNodeLocked(targetNodeId)) return;

        const beforeNodes = nodesRef.current;
        const beforeEdges = edgesRef.current;
        const targetNode = beforeNodes.find((node) => String(node.id) === String(targetNodeId));
        if (!targetNode || targetNode.type !== 'functionNode') return;

        const fromParamKey = getSlotParamKey(targetNode.data?.functionKey, fromSlotKey);
        const toParamKey = getSlotParamKey(targetNode.data?.functionKey, toSlotKey);

        const nextEdges = beforeEdges.map((edge) => {
            if (String(edge.target) !== String(targetNodeId)) return edge;

            const currentSlotKey = String(edge.data?.inputSlotKey || '');
            if (currentSlotKey !== String(fromSlotKey) && currentSlotKey !== String(toSlotKey)) {
                return edge;
            }

            const nextSlotKey = currentSlotKey === String(fromSlotKey)
                ? String(toSlotKey)
                : String(fromSlotKey);

            return {
                ...edge,
                data: {
                    ...(edge.data || {}),
                    inputSlotKey: nextSlotKey,
                },
            };
        });

        const nextNodesDraft = beforeNodes.map((node) => {
            if (String(node.id) !== String(targetNodeId) || node.type !== 'functionNode') return node;

            const previousPanelState = node.data?.panelState || {};
            const previousUploads = previousPanelState.uploads && typeof previousPanelState.uploads === 'object'
                ? previousPanelState.uploads
                : {};
            const previousLinkedSlotSources = (
                previousPanelState.linkedSlotSources
                && typeof previousPanelState.linkedSlotSources === 'object'
                && !Array.isArray(previousPanelState.linkedSlotSources)
            ) ? previousPanelState.linkedSlotSources : {};
            const previousInputParams = node.data?.inputParams || {};
            const previousRepaintMasks = (
                previousPanelState.repaintMasks
                && typeof previousPanelState.repaintMasks === 'object'
                && !Array.isArray(previousPanelState.repaintMasks)
            ) ? previousPanelState.repaintMasks : {};
            const repaintMaskParamKeyMap = FUNCTION_SLOT_REPAINT_MASK_PARAM_KEY_MAP[node.data?.functionKey] || {};
            const fromRepaintMaskParamKey = repaintMaskParamKeyMap[fromSlotKey];
            const toRepaintMaskParamKey = repaintMaskParamKeyMap[toSlotKey];

            const nextUploads = {
                ...previousUploads,
                [fromSlotKey]: previousUploads[toSlotKey] ?? '',
                [toSlotKey]: previousUploads[fromSlotKey] ?? '',
            };

            const nextLinkedSlotSources = {
                ...previousLinkedSlotSources,
                [fromSlotKey]: previousLinkedSlotSources[toSlotKey],
                [toSlotKey]: previousLinkedSlotSources[fromSlotKey],
            };
            const nextRepaintMasks = {
                ...previousRepaintMasks,
                [fromSlotKey]: previousRepaintMasks[toSlotKey],
                [toSlotKey]: previousRepaintMasks[fromSlotKey],
            };

            if (nextRepaintMasks[fromSlotKey] == null) delete nextRepaintMasks[fromSlotKey];
            if (nextRepaintMasks[toSlotKey] == null) delete nextRepaintMasks[toSlotKey];

            if (nextLinkedSlotSources[fromSlotKey] == null) {
                delete nextLinkedSlotSources[fromSlotKey];
            }
            if (nextLinkedSlotSources[toSlotKey] == null) {
                delete nextLinkedSlotSources[toSlotKey];
            }

            const nextInputParams = {
                ...previousInputParams,
            };

            if (fromParamKey || toParamKey) {
                const fromValue = fromParamKey ? previousInputParams[fromParamKey] : undefined;
                const toValue = toParamKey ? previousInputParams[toParamKey] : undefined;

                if (fromParamKey) nextInputParams[fromParamKey] = toValue ?? '';
                if (toParamKey) nextInputParams[toParamKey] = fromValue ?? '';
            }
            if (fromRepaintMaskParamKey || toRepaintMaskParamKey) {
                const fromMaskValue = fromRepaintMaskParamKey
                    ? previousInputParams[fromRepaintMaskParamKey]
                    : undefined;
                const toMaskValue = toRepaintMaskParamKey
                    ? previousInputParams[toRepaintMaskParamKey]
                    : undefined;

                if (fromRepaintMaskParamKey) nextInputParams[fromRepaintMaskParamKey] = toMaskValue ?? '';
                if (toRepaintMaskParamKey) nextInputParams[toRepaintMaskParamKey] = fromMaskValue ?? '';
            }

            return {
                ...node,
                data: {
                    ...node.data,
                    inputParams: nextInputParams,
                    panelState: {
                        ...previousPanelState,
                        uploads: nextUploads,
                        repaintMasks: nextRepaintMasks,
                        linkedSlotSources: nextLinkedSlotSources,
                    },
                },
            };
        });

        const nextNodes = syncConnectedTargetNodes(nextNodesDraft, nextEdges, {
            targetNodeId: String(targetNodeId),
        });

        if (
            JSON.stringify(nextEdges) === JSON.stringify(beforeEdges)
            && JSON.stringify(nextNodes) === JSON.stringify(beforeNodes)
        ) return;

        pushHistory();
        setNodes(nextNodes);
        setEdges(nextEdges);
        persistCanvasBatch({
            beforeNodes,
            beforeEdges,
            afterNodes: nextNodes,
            afterEdges: nextEdges,
        });
    }, [isNodeLocked, persistCanvasBatch, pushHistory, setEdges, setNodes, nodesRef, edgesRef]);

    return {
        handleNodeDataChange,
        handleImageNodeSizeChange,
        handleSwitchFunctionNode,
        handleNodeGenerateComplete,
        handleNodeResultSelect,
        handleConnect,
        handleConnectEnd,
        handleAddNode,
        handleQuickAddNode,
        handleDeleteNode,
        handleDeleteSelection,
        performDelete,
        confirmDeleteGroupsIfNeeded,
        handleEdgesChange,
        removeEdgesFromCanvas,
        handleDeleteEdge,
        handleRemoveFunctionLinkedUpload,
        handleReplaceFunctionLinkedUpload,
        handleRemoveCreativeLinkedUpload,
        handleReplaceCreativeLinkedUpload,
        handleEdgeSlotChange,
        handleSwapFunctionInputSlots,
    };
};